// ICOPE内容生成服务 - 基于WHO ICOPE手册的三层建议池系统
import { ComprehensiveAssessmentResult } from '../types/index';
import { knowledgeBaseService } from './knowledgeBase';
import { knowledgeBasedContentGenerator } from './knowledgeBasedContentGenerator';
import { universalAdviceContentGenerator } from './universalAdviceContentGenerator';

// WHO ICOPE手册中的完整12项通用建议包定义
export const UNIVERSAL_ADVICE_PACKAGE = {
  id: "universal_advice",
  target: "all_users", 
  description: "基于WHO ICOPE手册的完整12项通用健康建议，适用于所有老年人",
  totalAdviceCount: 12,
  allAdviceUIDs: [
    "ICOPE-C3-S3.1.3-Advice-PhysicalActivity", "ICOPE-C3-S3.1.3-Advice-HealthyDiet",
    "ICOPE-C3-S3.1.3-Advice-Hydration", "ICOPE-C3-S3.1.3-Advice-OralHealth",
    "ICOPE-C3-S3.1.3-Advice-SocialConnection", "ICOPE-C3-S3.1.3-Advice-DiseaseManagement",
    "ICOPE-C3-S3.1.3-Advice-Vaccination", "ICOPE-C3-S3.1.3-Advice-MentalHealth",
    "ICOPE-C3-S3.1.3-Advice-Sleep", "ICOPE-C3-S3.1.3-Advice-BrainHealth",
    "ICOPE-C3-S3.1.3-Advice-EyeHealth", "ICOPE-C3-S3.1.3-Advice-EarHealth"
  ],
};

// 评估项名称映射
const ASSESSMENT_ITEM_NAMES: { [key: string]: string } = {
  cognition: '认知能力',
  mobility: '行动能力',
  vitality: '营养状况',
  vision: '视力健康',
  hearing: '听力健康', 
  psychologicalCapacity: '心理健康',
  socialCareSupport: '社会照护支持',
  carerSupport: '照护者支持',
  urinaryIncontinence: '尿失禁'
};

// --- V2: 内容转化与优化模块 ---

const EMPATHY_TEMPLATES: { [key: string]: { intro: string; explanation: string } } = {
    mobility: {
        intro: "我们注意到您在行动能力方面需要一些支持，这在老年人中很常见，请不要担心。",
        explanation: "这意味着您在走路、上下楼或日常活动时可能会感到有些吃力。社区可以为您提供安全的锻炼项目，帮您逐步增强力量和平衡，让您更安心地活动。"
    },
    cognition: {
        intro: "我们留意到您的记忆力或注意力可能需要一些额外的关注。",
        explanation: "这是一个很好的机会来锻炼我们的大脑，让它保持年轻和活力。社区可以提供一些有趣又健脑的活动来帮助您，比如认知游戏和小组活动，同时也会给您的家人一些实用的支持方法。"
    },
    vitality: {
        intro: "评估结果提示，我们需要一起关注您的营养状况。",
        explanation: "这可能意味着您需要调整饮食来获取更多能量。社区的专业人员可以为您提供简单有效的饮食建议，帮助您改善营养，增强体力。"
    },
    vision: {
        intro: "您的视力似乎需要一些特别的关怀。",
        explanation: "确保看得清晰对于日常生活和安全都非常重要。社区可以帮助您进行视力筛查，并提供获取合适眼镜的建议。"
    },
    hearing: {
        intro: "评估结果显示，您的听力可能需要一些帮助。",
        explanation: "听得清楚能让您更好地与家人朋友沟通，享受生活。社区可以提供听力方面的建议和支持，帮助您改善沟通状况。"
    },
    psychologicalCapacity: {
        intro: "我们很关心您最近的情绪和感受。",
        explanation: "感到情绪低落是很正常的，重要的是寻求支持。社区有专业的服务和活动，可以帮助您进行压力管理和放松，改善心情。"
    },
    default: {
        intro: "我们注意到您在这个方面需要一些支持。",
        explanation: "这是一个改善健康的好机会，社区的专业人员可以为您提供科学的指导和帮助。"
    }
};


/**
 * V2: 优化后的内容转化函数
 * @param coreContent 从知识块提取的核心信息
 * @param domain 领域上下文
 * @returns 转化后的文本
 */
function transformContentForUser(coreContent: string, domain: string): string {
    const template = EMPATHY_TEMPLATES[domain] || EMPATHY_TEMPLATES.default;
    // 简单的融合策略：开场白 + 核心信息 + 解释
    // 在实际应用中，这里可以使用更复杂的自然语言生成模型
    const friendlyCoreContent = coreContent
        .replace(/提供/g, '为您提供')
        .replace(/实施/g, '帮助您进行')
        .replace(/建议/g, '的贴心建议');
        
    return `${template.intro} ${template.explanation} ${friendlyCoreContent}`;
}

function extractActionSteps(content: string | undefined): string[] {
    if (!content) return ["咨询社区卫生人员获取具体指导"];
    
    const steps: string[] = [];
    const potentialSteps = content.match(/[\-•]\s*(.*?)[。，]/g) || content.match(/[^，。]+(?:提供|进行|参与|确保|学习|练习|咨询|安排)[^，。]+/g) || [];

    potentialSteps.forEach(step => {
        let cleanStep = step.replace(/[\-•\s]/g, '').replace(/[。，]$/, '').trim();
        if (cleanStep.length > 20) {
            const keywords = ['提供', '进行', '参与', '确保', '学习', '练习', '咨询', '安排'];
            for (const keyword of keywords) {
                if (cleanStep.includes(keyword)) {
                    cleanStep = cleanStep.substring(cleanStep.indexOf(keyword));
                    if (cleanStep.length > 20) {
                        cleanStep = cleanStep.substring(0, 19) + '…';
                    }
                    break;
                }
            }
        }
        if (cleanStep.length > 5 && cleanStep.length <= 20) {
            steps.push(cleanStep);
        }
    });

    if (steps.length === 0) return ["咨询社区卫生人员获取具体指导"];
    
    return [...new Set(steps)].slice(0, 3);
}

function getDeepDiveContent(relatedUid: string): string {
    const chapterMatch = relatedUid.match(/ICOPE-C(\d+)-/);
    if (!chapterMatch) return "请咨询专业人员，了解更多关于此领域的详细信息和背景知识。";

    const chapterNumber = chapterMatch[1];
    const overviewUid = `ICOPE-C${chapterNumber}-Overview`;
    const overviewBlock = knowledgeBaseService.getKnowledgeBlock(overviewUid);

    if (!overviewBlock || !overviewBlock.content) {
        return "请咨询专业人员，了解更多关于此领域的详细信息和背景知识。";
    }

    const content = overviewBlock.content;
    const keyPointsMatch = content.match(/关键点\s*-\s*(.*)/s);
    let summary = keyPointsMatch ? keyPointsMatch[1] : content;
    
    summary = summary.split('。')[0] + '。';
    summary = summary.replace(/社区卫生工作者/g, '社区的专业人员').replace(/护理者/g, '家人');

    return summary.length > 100 ? summary.substring(0, 97) + '...' : summary;
}

// --- 核心建议生成函数 ---

export function generateUniversalAdvice(assessmentResults?: ComprehensiveAssessmentResult, userProfile?: any): any[] {
    try {
        // 使用新的智能通用建议内容生成器
        const enrichedAdvice = universalAdviceContentGenerator.generateAllAdviceContent(userProfile);
        
        if (enrichedAdvice.length > 0) {
            return enrichedAdvice;
        }
    } catch (error) {
        console.error('智能通用建议生成失败，使用备用方案:', error);
    }
    
    // 备用方案：使用原始方法
    const allAdviceUIDs = UNIVERSAL_ADVICE_PACKAGE.allAdviceUIDs;
    const universalBlocks = knowledgeBaseService.getKnowledgeBlocks(allAdviceUIDs);
    
    return universalBlocks.map((block: any) => ({
        id: block.uid,
        title: block.title,
        coreContent: block.content || "暂无详细建议",
        extendedReading: knowledgeBaseService.getRelatedBlocks(block.uid).map((r: any) => ({
            uid: r.uid, title: r.title, content: r.content, tags: r.tags,
            source_chapter: r.source_chapter, domain: r.domain
        })),
        adviceNumber: UNIVERSAL_ADVICE_PACKAGE.allAdviceUIDs.indexOf(block.uid) + 1,
    }));
}

export function generatePassAdvice(itemName: string, result: any, userProfile?: any): any {
    const chineseName = ASSESSMENT_ITEM_NAMES[itemName] || itemName;
    const template = `您在“${chineseName}”方面的评估结果正常，这很棒！为了持续跟踪您的健康状况，我们建议您在以下情况出现时，及时进行ICOPE基础再评估：\n\n1. 根据您的照护计划中建议的复评时间。\n2. 当您的身体状况发生明显变化时（例如，最近生病、意外跌倒或住院后）。\n3. 当您的生活环境或状态发生重要变化时（例如，失去亲友、搬家或更换照护者）。`;

    return {
        id: `pass_${itemName}`,
        title: `${chineseName} - 维持良好状态`,
        category: 'pass',
        content: template,
        isPrioritized: false,
    };
}

export function generateFailAdvice(itemName: string, result: any, userProfile?: any): any {
    const chineseName = ASSESSMENT_ITEM_NAMES[itemName] || itemName;
    
    try {
        // 使用新的智能内容生成器
        const adviceContent = knowledgeBasedContentGenerator.generateAdviceContent(itemName, userProfile);
        
        return {
            id: `fail_${itemName}`,
            title: `${chineseName} - 需要关注`,
            category: 'fail',
            isPrioritized: false,
            ...adviceContent
        };
    } catch (error) {
        console.error('智能内容生成失败，使用备用内容:', error);
        
        // 备用方案：使用简化的内容生成
        return {
            id: `fail_${itemName}`,
            title: `${chineseName} - 需要关注`,
            category: 'fail',
            isPrioritized: false,
            communityIntervention: { 
                content: `我们关注您的${chineseName}状况。社区可以为您提供专业的支持和指导，帮助您改善这方面的健康状况。` 
            },
            actionSteps: ['咨询社区专业人员获取指导', '制定个人改善计划', '定期跟进评估'],
            deepDive: {
                content: '请咨询专业人员了解更多关于此领域的详细信息和背景知识。',
                knowledgeBlocks: []
            },
            professionalReferral: {
                content: `您的"${chineseName}"评估结果提示需要进一步关注。我们建议您就此问题寻求医疗机构的专业评估，以获得更个性化的指导和支持。`,
                urgency: "建议优先处理"
            }
        };
    }
}

export function generatePersonalizedContent(
  assessmentResults: ComprehensiveAssessmentResult,
  userProfile?: any
): {
  universalAdvice: any[];
  specificAdvice: any[];
  overallSummary: any;
} {
  const finalReport = {
    universalAdvice: generateUniversalAdvice(assessmentResults, userProfile),
    specificAdvice: [] as any[]
  };

  const allResults = [
    ...Object.entries(assessmentResults.intrinsicCapacity),
    ...Object.entries(assessmentResults.keyFactors).filter(([_, result]) => result)
  ];

  for (const [itemName, result] of allResults) {
    let advice = null;
    const status = result?.status || 'UNKNOWN';

    if (['PASS', 'OK', 'SUPPORTED'].includes(status)) {
      advice = generatePassAdvice(itemName, result, userProfile);
    } else if (['FAIL', 'NEEDS_ATTENTION'].includes(status)) {
      advice = generateFailAdvice(itemName, result, userProfile);
    }
    
    if (advice) {
      finalReport.specificAdvice.push(advice);
    }
  }

  const failedItems = finalReport.specificAdvice.filter(a => a.category === 'fail').length;
  const passedItems = finalReport.specificAdvice.filter(a => a.category === 'pass').length;
  
  const overallSummary = {
    totalAssessments: finalReport.specificAdvice.length,
    passedAssessments: passedItems,
    failedAssessments: failedItems,
    overallStatus: assessmentResults.overallStatus,
    priorityRecommendations: finalReport.specificAdvice
      .filter(a => a.category === 'fail')
      .map(a => a.title),
    nextSteps: failedItems > 0 ? 
      ["建议优先处理需要关注的项目", "寻求专业医疗建议", "制定个性化改善计划"] :
      ["继续保持良好状态", "定期进行健康监测", "维持健康生活方式"]
  };

  return { ...finalReport, overallSummary };
}
